import { Search, Sun, Moon, Plus, Menu, Wifi, WifiOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useTasks } from "../contexts/TaskContext";
import { workspaceFromPath, getWorkspace } from "../lib/workspaces";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export default function Header({ onNewTask, search, setSearch, onMenuClick }) {
  const { theme, toggle } = useTheme();
  const { wsStatus } = useTasks();
  const [online, setOnline] = useState(navigator.onLine);
  const location = useLocation();
  const wsKey = workspaceFromPath(location.pathname);
  const currentWorkspace = wsKey ? getWorkspace(wsKey) : null;

  useEffect(() => {
    const goOn = () => setOnline(true);
    const goOff = () => setOnline(false);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    return () => {
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
    };
  }, []);

  let indicator;
  if (!online) {
    indicator = { label: "Offline", icon: <WifiOff size={12} />, cls: "border-amber-500/30 text-amber-500 bg-amber-500/10" };
  } else if (wsStatus === "open") {
    indicator = { label: "Live", icon: <Wifi size={12} />, cls: "border-emerald-500/30 text-emerald-500 bg-emerald-500/10" };
  } else if (wsStatus === "connecting") {
    indicator = { label: "Syncing", icon: <Loader2 size={12} className="animate-spin" />, cls: "border-blue-500/30 text-blue-500 bg-blue-500/10" };
  } else {
    indicator = { label: "Reconnecting", icon: <WifiOff size={12} />, cls: "border-amber-500/30 text-amber-500 bg-amber-500/10" };
  }

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-md"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="h-16 flex items-center px-4 sm:px-6 gap-3">
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        onClick={onMenuClick}
        data-testid="header-menu-btn"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </Button>

      {/* Current workspace identity — prominent, always visible when in a dashboard */}
      {currentWorkspace && (
        <div
          data-testid="header-workspace-badge"
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors duration-300"
          style={{
            borderColor: `${currentWorkspace.color}40`,
            backgroundColor: `${currentWorkspace.color}10`,
            color: currentWorkspace.color,
          }}
        >
          <currentWorkspace.icon size={14} strokeWidth={2} />
          <span className="text-xs font-display font-bold tracking-tight">
            {currentWorkspace.fullLabel}
          </span>
        </div>
      )}

      <div className="flex-1 max-w-xl relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.7} />
        <Input
          data-testid="global-search-input"
          placeholder="Search tasks, notes, tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-10 bg-secondary/40 border-border focus-visible:ring-1 focus-visible:ring-primary/60"
        />
      </div>

      <div className="flex items-center gap-2">
        <div
          data-testid="ws-indicator"
          title={`WebSocket: ${wsStatus}`}
          className={`hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${indicator.cls}`}
        >
          {indicator.icon}
          <span>{indicator.label}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-btn" aria-label="Toggle theme">
          {theme === "dark" ? <Sun size={17} strokeWidth={1.7} /> : <Moon size={17} strokeWidth={1.7} />}
        </Button>
        <Button
          onClick={onNewTask}
          className="gap-1.5 h-10 px-4 font-medium transition-colors duration-300"
          data-testid="header-new-task-btn"
          style={
            currentWorkspace
              ? {
                  backgroundColor: currentWorkspace.color,
                  color: "#fff",
                }
              : {}
          }
        >
          <Plus size={16} strokeWidth={2} />
          <span className="hidden sm:inline">New Task</span>
        </Button>
      </div>
      </div>
    </header>
  );
}
